import React, { useState, useEffect } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import Endpoints from '../../../../constants/Endpoints'
import Helmet from 'react-helmet'
import AppHeader from '../../../../components/AppView/Header'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { AppMainView } from '../../../../components/AppView'
import { InventoryLogsTable } from '../../../../components/InventoryLogs/InventoryLogsTable'
import { getLocationsWithCRSA, parseErrorMsg } from '../../../../utilities/helpers'
import { message } from 'antd'
import * as ocsAPI from '@getgreenline/ocs'
import {
  CustomTablePaginationV2,
  TPaginationPosition,
} from '../../../../components/CustomTablePaginationV2'
import { ICursorPagination } from '@getgreenline/shared'
import { InventoryLogModels } from '@getgreenline/inventory'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { ILocationContract } from '@getgreenline/locations/build/models'
import { BlazeCRSALocationSelector, ICRSALocations } from '../BlazeCRSALocationSelector'
import { parseSkFromLastEvaluatedKey } from '../../../../utilities/dynamoDbPaginationHelpers'

interface InjectedProps {
  currentCompanyStore: CurrentCompanyStore
}

export interface IProcessOnDateData {
  processOnDate: string
  inventoryLogId: string
}

const _ProcessOnDateLogs = ({ currentCompanyStore }: InjectedProps) => {
  const [locationsWithCRSA, setLocationsWithCRSA] = useState<ILocationContract[]>([])
  const [selectedCRSA, setSelectedCRSA] = useState<ICRSALocations | undefined>()
  const [hashKeys, setHashKeys] = useState<ocsAPI.ICreateHashKeyRequestContract[]>([])
  const [logs, setLogs] = useState<InventoryLogModels.IInventoryLog[]>([])
  const [processOnDateData, setProcessOnDateData] = useState<IProcessOnDateData[]>([])
  const [pagination, setPagination] = useState<ICursorPagination>({
    nextCursor: null,
    prevCursor: null,
  })
  const [lastEvaluatedKeys, setLastEvaluatedKeys] = useState<string[]>([])
  const [paginationReset, setPaginationReset] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const limit = 20
  const company = currentCompanyStore.company
  const companyId = company?.id

  const sortAndSetProcessOnDateData = (logs: ocsAPI.IGetProcessOnDateLogsResponseContract) => {
    const data = logs.items.map((item) => ({
      processOnDate: item.processOnDate,
      inventoryLogId: item.inventoryLogId,
    }))
    setProcessOnDateData(data)
  }

  const sortAndSetInventoryLogs = (logs: ocsAPI.IGetProcessOnDateLogsResponseContract) => {
    const inventoryLogs = logs.items.map((item) => item.inventoryLog)
    setLogs(inventoryLogs)
  }

  const loadLogs = async (cursor?: string | null, updatedPrevCursor?: string | null) => {
    setIsLoading(true)
    setLogs([])
    try {
      if (!company) {
        throw new Error('No identified company')
      }

      const hashKeyObj = hashKeys.find((obj) => obj.retailerCRSA === selectedCRSA?.crsa)
      const hashKey = hashKeyObj?.hashKey || ''

      const tenantHashKeyPathParams: ocsAPI.ITenantHashKeyPathParams = {
        gateway: Endpoints.AUTHZ_GATEWAY_URL,
        tenantId: `${companyId}`,
        hashKey,
      }

      // Can't send directly the lastEvaluatedKey as the exclusiveStartKey while using API Gateway mapping templates
      // Need to deconstruct using the parseSkFromLastEvaluatedKey function, and recreate the full exclusiveStartKey in api
      const paginationFilters = cursor
        ? { limit, exclusiveStartKeySk: parseSkFromLastEvaluatedKey(cursor) }
        : { limit }

      const fullLogs = await ocsAPI.getProcessOnDateLogs(tenantHashKeyPathParams, paginationFilters)
      sortAndSetProcessOnDateData(fullLogs)
      sortAndSetInventoryLogs(fullLogs)
      setPagination({
        nextCursor: fullLogs.lastEvaluatedKey || null,
        prevCursor: updatedPrevCursor ? updatedPrevCursor : null,
      })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading process on date logs')
    } finally {
      setIsLoading(false)
    }
  }

  const changePage = async (cursor?: string | null, updatedLastEvaluatedKeys?: string[]) => {
    const isUserFurtherThanPageTwo =
      updatedLastEvaluatedKeys && updatedLastEvaluatedKeys.length >= 2

    // If user is on page 2, prevCursor will be null (= we're making the api call to get the logs without an exclusiveStartKey, which returns the most recent logs needed to display on page 1)
    const updatedPrevCursor =
      updatedLastEvaluatedKeys && isUserFurtherThanPageTwo
        ? updatedLastEvaluatedKeys[updatedLastEvaluatedKeys.length - 2]
        : null
    setLastEvaluatedKeys(updatedLastEvaluatedKeys || [])
    await loadLogs(cursor, updatedPrevCursor)
  }

  const setPaginationFirstPage = (paginationReset: boolean) => {
    setPaginationReset(paginationReset)
  }

  const resetPageData = () => {
    setSelectedCRSA(undefined)
    setLogs([])
    setPaginationReset(true)
    setLastEvaluatedKeys([])
    setPagination({
      nextCursor: null,
      prevCursor: null,
    })
  }

  useEffect(() => {
    setLocationsWithCRSA(getLocationsWithCRSA(currentCompanyStore?.locations))
    setHashKeys(currentCompanyStore?.globalHashKeys || [])
  }, [currentCompanyStore?.globalHashKeys])

  return (
    <div className='d-flex flex-column h-100'>
      <Helmet>
        <title>Compliance | Process on Date Logs</title>
        <meta name='page-loaded' content='true' />
      </Helmet>

      <AppHeader title='Process on Date Logs' />

      <AppMainView className='background-grey intercom-pb-2'>
        <div className='white-rounded-container d-flex mt-1' style={{ flexWrap: 'wrap' }}>
          <BlazeCRSALocationSelector
            locations={locationsWithCRSA}
            value={selectedCRSA ? selectedCRSA.crsa : undefined}
            onSelect={({ crsaLocation }) => {
              setSelectedCRSA(crsaLocation)
            }}
          />
          <BlazeButton
            buttonProps={{
              loading: isLoading,
              disabled: isLoading || !selectedCRSA || !hashKeys,
              type: 'primary',
              onClick: () => {
                loadLogs(), setPaginationReset(true), setLastEvaluatedKeys([])
              },
            }}
            titleProps={{
              title: isLoading ? 'Applying...' : 'Apply',
            }}
          />
          <BlazeButton
            buttonProps={{
              type: 'link',
              disabled: !selectedCRSA,
              onClick: resetPageData,
            }}
            titleProps={{ title: 'Reset' }}
          />
        </div>
        {companyId && (
          <CustomTablePaginationV2
            limit={limit}
            hideRowOptions={true}
            changePage={(cursor, updatedLastEvaluatedKeys) =>
              changePage(cursor, updatedLastEvaluatedKeys)
            }
            data={logs || []}
            paginationPosition={TPaginationPosition.TOP_RIGHT}
            pagination={pagination}
            lastEvaluatedKeys={lastEvaluatedKeys}
            paginationReset={paginationReset}
            setPaginationFirstPage={setPaginationFirstPage}
          >
            <InventoryLogsTable
              companyId={companyId}
              logs={logs}
              showProductColumn
              onChange={loadLogs}
              isLoading={isLoading}
              processOnDateData={processOnDateData}
            />
          </CustomTablePaginationV2>
        )}
      </AppMainView>
    </div>
  )
}

export const ProcessOnDateLogs = inject('currentCompanyStore')(observer(_ProcessOnDateLogs))
